import React, { useState, useEffect } from 'react';

const API_URL = '';

export default function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Products data:', data);
      
      let productsArray = [];
      if (Array.isArray(data)) {
        productsArray = data;
      } else if (data.products && Array.isArray(data.products)) {
        productsArray = data.products;
      } else if (data.data && Array.isArray(data.data)) {
        productsArray = data.data;
      } else {
        productsArray = [];
        console.warn('Unexpected data format:', data);
      }
      
      setProducts(productsArray);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setLoading(false);
    }
  };

  // ========== ADD THIS DELETE FUNCTION ==========
  const handleDelete = async (productId) => {
    console.log('🗑️ DELETE button clicked, ID:', productId);

    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        // Remove the product from the state
        setProducts(prev => prev.filter(p => p.id !== productId));
        alert('✅ Product deleted successfully!');
      } else {
        const errorData = await response.json();
        console.error('Delete failed:', errorData);
        alert(`❌ Delete failed: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('❌ Error deleting product: ' + error.message);
    }
  };

  // ========== ADD EDIT FUNCTION (placeholder for now) ==========
  const handleEdit = (product) => {
    console.log('✏️ Edit product:', product);
    alert(`Edit functionality coming soon! Product: ${product.name}`);
    // You can implement edit modal here later
  };

  // Helper function to safely get image URL
  const getProductImage = (product) => {
    if (!product) return '/images/placeholder.jpg';
    
    if (product.image && typeof product.image === 'string') {
      return product.image.startsWith('/') ? product.image : `/${product.image}`;
    }
    if (product.images?.full) return product.images.full;
    if (product.images?.modal) return product.images.modal;
    
    return '/images/placeholder.jpg';
  };

  // Safely filter products
  const filteredProducts = Array.isArray(products) 
    ? (filter === 'all' ? products : products.filter(p => p.type === filter))
    : [];

  const categories = ['all', 'album', 'novel', 'art', 'tshirt', 'cap', 'hoodie'];

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p style={{ color: '#fff', marginTop: '20px' }}>Loading products...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Products Manager</h2>
          <p style={styles.subtitle}>Manage all your store products</p>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statBadge}>
            <span style={styles.statNumber}>{products.length || 0}</span>
            <span style={styles.statLabel}>Total Products</span>
          </div>
          <div style={styles.statBadge}>
            <span style={styles.statNumber}>
              {Array.isArray(products) ? products.filter(p => p.featured).length : 0}
            </span>
            <span style={styles.statLabel}>Featured</span>
          </div>
        </div>
      </div>

      <div style={styles.filters}>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            style={{
              ...styles.filterButton,
              ...(filter === cat ? styles.filterButtonActive : {})
            }}
          >
            {cat === 'all' ? 'All Products' : cat.charAt(0).toUpperCase() + cat.slice(1) + 's'}
            <span style={styles.filterCount}>
              {cat === 'all' 
                ? (products.length || 0)
                : (Array.isArray(products) ? products.filter(p => p.type === cat).length : 0)}
            </span>
          </button>
        ))}
      </div>

      {filteredProducts.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📦</span>
          <p style={styles.emptyText}>No products found</p>
          <p style={styles.emptySubtext}>Add your first product to get started!</p>
        </div>
      ) : (
        <div style={styles.productsGrid}>
          {filteredProducts.map(product => (
            <div key={product.id} style={styles.productCard}>
              <div style={styles.imageWrapper}>
                <img 
                  src={getProductImage(product)}
                  alt={product.name || 'Product'}
                  style={styles.productImage}
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
                {product.featured && (
                  <div style={styles.featuredBadge}>⭐ Featured</div>
                )}
              </div>
              
              <div style={styles.productInfo}>
                <h3 style={styles.productName}>{product.name || 'Untitled'}</h3>
                <div style={styles.productMeta}>
                  <span style={styles.productType}>{product.type || 'product'}</span>
                  <span style={styles.productPrice}>
                    ${((product.price || 0) / 100).toFixed(2)}
                  </span>
                </div>
                
                {product.preorderGoal && product.preorders !== undefined && (
                  <div style={styles.preorderProgress}>
                    <div style={styles.progressBar}>
                      <div 
                        style={{
                          ...styles.progressFill,
                          width: `${Math.min((product.preorders / product.preorderGoal) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <span style={styles.progressText}>
                      {product.preorders || 0} / {product.preorderGoal} preorders
                    </span>
                  </div>
                )}

                <div style={styles.productActions}>
                  <button style={styles.editButton} onClick={() => handleEdit(product)}>
                    ✏️ Edit
                  </button>
                  <button style={styles.deleteButton} onClick={() => handleDelete(product.id)}>
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Styles (keep all your existing styles - they are fine)
const styles = {
  container: {
    padding: '30px',
    backgroundColor: '#0a0a0a',
    minHeight: '100vh',
    color: '#fff'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    backgroundColor: '#0a0a0a'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(206, 0, 33, 0.1)',
    borderTop: '4px solid #ce0021',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#ce0021',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#999',
    margin: 0
  },
  headerStats: {
    display: 'flex',
    gap: '16px'
  },
  statBadge: {
    background: 'rgba(206, 0, 33, 0.1)',
    border: '1px solid rgba(206, 0, 33, 0.3)',
    borderRadius: '12px',
    padding: '12px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ce0021'
  },
  statLabel: {
    fontSize: '12px',
    color: '#999'
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '30px',
    flexWrap: 'wrap'
  },
  filterButton: {
    padding: '10px 20px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#999',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  filterButtonActive: {
    background: 'rgba(206, 0, 33, 0.2)',
    border: '1px solid rgba(206, 0, 33, 0.5)',
    color: '#ce0021'
  },
  filterCount: {
    background: 'rgba(206, 0, 33, 0.3)',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '12px'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'rgba(255, 255, 255, 0.02)',
    borderRadius: '16px',
    border: '1px dashed rgba(255, 255, 255, 0.1)'
  },
  emptyIcon: {
    fontSize: '64px',
    display: 'block',
    marginBottom: '16px'
  },
  emptyText: {
    fontSize: '20px',
    color: '#fff',
    margin: '0 0 8px 0'
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#999',
    margin: 0
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '24px'
  },
  productCard: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
    border: '1px solid rgba(206, 0, 33, 0.2)',
    borderRadius: '16px',
    overflow: 'hidden',
    transition: 'all 0.3s'
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: '200px',
    overflow: 'hidden',
    background: '#000'
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  featuredBadge: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'rgba(206, 0, 33, 0.9)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  productInfo: {
    padding: '16px'
  },
  productName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    margin: '0 0 12px 0'
  },
  productMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  productType: {
    fontSize: '12px',
    color: '#999',
    textTransform: 'uppercase',
    background: 'rgba(255, 255, 255, 0.05)',
    padding: '4px 10px',
    borderRadius: '4px'
  },
  productPrice: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#ce0021'
  },
  preorderProgress: {
    marginBottom: '12px'
  },
  progressBar: {
    width: '100%',
    height: '6px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '6px'
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #ce0021 0%, #ff6b35 100%)',
    transition: 'width 0.3s'
  },
  progressText: {
    fontSize: '12px',
    color: '#999'
  },
  productActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px'
  },
  editButton: {
    flex: 1,
    padding: '10px',
    background: 'rgba(206, 0, 33, 0.1)',
    border: '1px solid rgba(206, 0, 33, 0.3)',
    borderRadius: '8px',
    color: '#ce0021',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s'
  },
  deleteButton: {
    flex: 1,
    padding: '10px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#999',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s'
  }
};

// Add the keyframes animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);